"use client";

/**
 * Route Error Boundary
 *
 * Catches errors thrown by route segments under the root layout. Unlike
 * `global-error.tsx`, this renders within the existing layout so it can
 * use shared components like Button and Link.
 */

import { useEffect } from "react";
import Link from "next/link";
import { captureException } from "@/lib/analytics";
import { Button } from "@/components/ui/Button";
import { AlertTriangle, Home, RotateCcw } from "lucide-react";
import { routes } from "@/lib/routes";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    captureException(error, {
      digest: error.digest,
      source: "route_error_boundary",
    });
  }, [error]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="mb-6 flex justify-center">
          <div className="rounded-full bg-red-100 p-4">
            <AlertTriangle className="h-10 w-10 text-red-600" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Etwas ist schiefgelaufen</h1>
        <p className="text-gray-600 mb-8">
          Wir haben den Fehler erfasst und arbeiten daran. Bitte versuchen Sie es erneut.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={reset} leftIcon={<RotateCcw className="h-4 w-4" />}>
            Erneut versuchen
          </Button>
          <Link href={routes.home}>
            <Button variant="outline" leftIcon={<Home className="h-4 w-4" />}>
              Zur Startseite
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
